/**
 * Pure helpers for Settings → Webhooks (unit-tested in webhook-format.test.ts).
 *
 * Mirrors the server rules in internal/services/webhooks/generic.go:
 *   - an event type is lowercase letters / digits / underscores / dots,
 *     optionally ending in ".*" — a wildcard over a dotted family
 *     ("invoice.*" covers invoice.paid, invoice.posted, …);
 *   - a confidential type (POSH, loan_*, tax_*) is never delivered by "all
 *     events" or a wildcard — only when picked by its exact name.
 */

/** Mirrors webhooks.ValidateEventTypes (eventTypeRE) on the server. */
export const EVENT_TYPE_RE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*(\.\*)?$/;

export const MAX_EVENT_TYPE_LEN = 40;

/** A ".*" wildcard subscription entry. */
export function isEventPattern(t: string): boolean {
  return t.endsWith(".*");
}

/** Whether subscription entry `sub` (exact or ".*") covers `eventType`. */
export function eventMatches(sub: string, eventType: string): boolean {
  if (sub === eventType) return true;
  if (!isEventPattern(sub)) return false;
  return eventType.startsWith(sub.slice(0, -1));
}

/** Normalises + validates one typed-in event type; null when invalid. */
export function normaliseEventType(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  if (!t || t.length > MAX_EVENT_TYPE_LEN || !EVENT_TYPE_RE.test(t)) return null;
  return t;
}

/**
 * Whether a saved Slack / Teams category is one the catalog offers ("*",
 * an exact type, or a listed ".*" family). An unknown one is not
 * necessarily dead — the server accepts any well-formed type — but the
 * catalog cannot vouch that anything sends it.
 */
export function isCatalogCategory(
  value: string,
  catalog: readonly { type: string }[],
): boolean {
  return value === "*" || catalog.some((e) => e.type === value);
}

/**
 * The delivery-log rows that get a "Redeliver" button: rows whose delivery
 * is still held (redeliverable), one per delivery — its newest row (the
 * log is newest first).
 */
export function redeliverRowIds(
  rows: readonly {
    id: number;
    delivery_id?: string | null;
    redeliverable?: boolean;
  }[],
): Set<number> {
  const seen = new Set<string>();
  const out = new Set<number>();
  for (const r of rows) {
    if (!r.redeliverable || !r.delivery_id || seen.has(r.delivery_id)) continue;
    seen.add(r.delivery_id);
    out.add(r.id);
  }
  return out;
}

/** Human label for a parked delivery's reason. */
export function deadLetterReasonLabel(reason: string): string {
  switch (reason) {
    case "queue_full":
      return "retry queue was full";
    case "exhausted":
      return "all retries failed";
    case "rejected":
      return "endpoint rejected it";
    case "endpoint_disabled":
      return "webhook was turned off";
    default:
      return reason;
  }
}

/** Validates the delivery-log retention (whole days within the bounds). */
export function validRetentionDays(
  raw: string,
  min: number,
  max: number,
): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n >= min && n <= max ? n : null;
}
